<template>
  <el-dialog
    v-model="dialogVisible"
    width="420px"
    :close-on-click-modal="true"
    class="pay-dialog"
    :show-close="true"
    center
    align-center
  >
    <template #header>
      <div class="pay-dialog-header">
        <span class="pay-dialog-icon">&#x1F48E;</span>
        <h3 class="pay-dialog-title">积分不足</h3>
      </div>
    </template>

    <div class="pay-dialog-body">
      <!-- Info -->
      <div class="pay-dialog-info">
        <p class="pay-dialog-desc">
          {{ feature ? `${feature}需要` : '该功能需要' }}
          <span class="pay-dialog-cost">{{ cost }}</span>
          积分
        </p>
        <p class="pay-dialog-balance">当前余额: {{ balance }} 积分</p>
      </div>

      <!-- Divider -->
      <div class="pay-dialog-divider">获取积分的方式</div>

      <!-- Options -->
      <div class="pay-dialog-options">
        <!-- Option 1: Direct pay -->
        <div class="pay-dialog-option" @click="handleDirectPay">
          <div class="pay-dialog-option-left">
            <span class="pay-dialog-option-icon">&#x1F4B0;</span>
            <div class="pay-dialog-option-text">
              <span class="pay-dialog-option-title">{{ rmbPrice > 0 ? rmbPrice + '元 直接解读' : '直接解读' }}</span>
              <span class="pay-dialog-option-sub">无需积分，即买即用</span>
            </div>
          </div>
          <span class="pay-dialog-badge pay-dialog-badge-soon">即将上线</span>
        </div>

        <!-- Option 2: Earn points -->
        <div
          class="pay-dialog-option pay-dialog-option--primary"
          @click="handleGoPoints"
        >
          <div class="pay-dialog-option-left">
            <span class="pay-dialog-option-icon">&#x1F48E;</span>
            <div class="pay-dialog-option-text">
              <span class="pay-dialog-option-title">去赚取积分</span>
              <span class="pay-dialog-option-sub">签到 / 邀请 / 完成任务</span>
            </div>
          </div>
          <span class="pay-dialog-arrow">&rsaquo;</span>
        </div>

        <!-- Option 3: Membership -->
        <div class="pay-dialog-option" @click="handleGoMembership">
          <div class="pay-dialog-option-left">
            <span class="pay-dialog-option-icon">&#x1F451;</span>
            <div class="pay-dialog-option-text">
              <span class="pay-dialog-option-title">开通使者 28元/月</span>
              <span class="pay-dialog-option-sub">AI解读不限次 + 更多权益</span>
            </div>
          </div>
          <span class="pay-dialog-badge pay-dialog-badge-soon">即将上线</span>
        </div>
      </div>

      <!-- Tip -->
      <div class="pay-dialog-tip">
        免费赚取：签到 +3~23 / 邀请 +30
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  cost: {
    type: Number,
    default: 0,
  },
  balance: {
    type: Number,
    default: 0,
  },
  feature: {
    type: String,
    default: '',
  },
  /** 人民币直购价（元），由调用方经动态查价传入；不传时降级为不带价格的文案 */
  rmbPrice: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits(['update:visible', 'goPoints', 'goMembership'])

const dialogVisible = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val),
})

function handleGoPoints() {
  dialogVisible.value = false
  emit('goPoints')
}

function handleGoMembership() {
  dialogVisible.value = false
  emit('goMembership')
}

function handleDirectPay() {
  // Direct pay not yet online, redirect to points center
  dialogVisible.value = false
  emit('goPoints')
}
</script>

<style scoped>
/* Dialog container */
.pay-dialog :deep(.el-dialog) {
  background: var(--fh-surface);
  border: 1px solid var(--fh-border-strong);
  border-radius: var(--radius-lg);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.3);
}

.pay-dialog :deep(.el-dialog__header) {
  padding: 28px 28px 0;
  margin: 0;
}

.pay-dialog :deep(.el-dialog__body) {
  padding: 16px 28px 28px;
}

.pay-dialog :deep(.el-dialog__headerbtn .el-dialog__close) {
  color: var(--fh-text-muted);
  font-size: 18px;
}

.pay-dialog :deep(.el-dialog__headerbtn:hover .el-dialog__close) {
  color: var(--fh-text);
}

/* Header */
.pay-dialog-header {
  text-align: center;
}

.pay-dialog-icon {
  font-size: 40px;
  line-height: 1;
  display: block;
  margin-bottom: 8px;
}

.pay-dialog-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--fh-text);
  margin: 0;
  letter-spacing: 0.02em;
}

/* Info */
.pay-dialog-info {
  text-align: center;
  margin-bottom: 20px;
}

.pay-dialog-desc {
  font-size: 0.9rem;
  color: var(--fh-text-secondary);
  margin: 0 0 4px;
}

.pay-dialog-cost {
  font-weight: 700;
  color: var(--fh-accent);
}

.pay-dialog-balance {
  font-size: 0.8rem;
  color: var(--fh-text-muted);
  margin: 0;
}

/* Divider */
.pay-dialog-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  font-size: 0.8rem;
  color: var(--fh-text-muted);
}

.pay-dialog-divider::before,
.pay-dialog-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--fh-border);
}

/* Options */
.pay-dialog-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}

.pay-dialog-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-radius: var(--radius-md);
  background: var(--fh-input-bg);
  border: 1px solid var(--fh-border);
  cursor: pointer;
  transition: all 0.2s ease;
}

.pay-dialog-option:hover {
  background: var(--fh-card-bg-hover);
  border-color: var(--fh-border-strong);
}

.pay-dialog-option:active {
  transform: scale(0.98);
}

.pay-dialog-option--primary {
  border-color: var(--fh-accent);
  background: var(--fh-accent-bg);
}

.pay-dialog-option--primary:hover {
  background: var(--fh-accent-bg);
  border-color: var(--fh-accent-hover);
}

.pay-dialog-option-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.pay-dialog-option-icon {
  font-size: 24px;
  line-height: 1;
  flex-shrink: 0;
}

.pay-dialog-option-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pay-dialog-option-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--fh-text);
}

.pay-dialog-option-sub {
  font-size: 0.75rem;
  color: var(--fh-text-muted);
}

.pay-dialog-arrow {
  font-size: 1.2rem;
  color: var(--fh-text-muted);
  font-weight: 300;
  flex-shrink: 0;
}

/* Badge */
.pay-dialog-badge {
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 0.7rem;
  flex-shrink: 0;
  white-space: nowrap;
}

.pay-dialog-badge-soon {
  background: rgba(251, 191, 36, 0.12);
  color: #d97706;
  border: 1px solid rgba(251, 191, 36, 0.2);
}

/* Tip */
.pay-dialog-tip {
  text-align: center;
  font-size: 0.75rem;
  color: var(--fh-text-muted);
  padding-top: 4px;
}

/* Overlay */
:deep(.el-overlay) {
  background-color: var(--fh-overlay);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}
</style>
